/**
 * Urgency, impact and the priority matrix, as the ticket screens use them.
 *
 * Urgency is how soon it hurts and impact how widely; both are a three-step
 * scale. When either is set without a priority, the server gives the ticket
 * the priority its grid names for the pair (see the API's priority-matrix.ts).
 * The grid comes with the ticket form's metadata, for agents only, so a form
 * can show the priority that will follow before anything is sent — the server
 * still decides, and its answer is what the screen keeps.
 */

export type Level = 1 | 2 | 3;

/** Low to high, the order a select lists them in. */
export const LEVEL_CHOICES: ReadonlyArray<{ value: Level; label: string }> = [
  { value: 1, label: 'Low' },
  { value: 2, label: 'Medium' },
  { value: 3, label: 'High' },
];

/** What a ticket holds when nobody has set one: the column default. */
export const DEFAULT_LEVEL: Level = 2;

export function levelLabel(value: unknown): string | null {
  const level = Number(value);
  return LEVEL_CHOICES.find((choice) => choice.value === level)?.label ?? null;
}

export interface FormPriorityMatrix {
  enabled: boolean;
  /** Each pair's priority key, or null where the pair derives nothing. */
  cells: Array<{ urgency: number; impact: number; priority: string | null }>;
}

/**
 * The priority key the grid gives a pair, or null when the grid is off, is not
 * known, or derives nothing for that pair — in which case the priority stays
 * as it would otherwise be.
 */
export function matrixPriority(
  matrix: FormPriorityMatrix | null | undefined,
  urgency: number,
  impact: number,
): string | null {
  if (!matrix?.enabled) return null;
  return matrix.cells.find((cell) => cell.urgency === urgency && cell.impact === impact)?.priority ?? null;
}
